import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckSquare, ChevronDown, ChevronRight, Image, ListTree, MessageSquare, PanelLeftClose, Square, Trash2 } from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential } from 'firebase/auth';
import { auth } from '../config/firebase';
import { Annotation } from '../types';
import { addAnnotation, deleteAnnotation, setAnnotationResolved, subscribeToAnnotations } from '../services/annotations';
import './PdfOutlinePanel.css';

// A PDF.js outline item (subset of fields we use)
interface OutlineItem {
  title: string;
  dest: string | any[] | null;
  url: string | null;
  items: OutlineItem[];
}

// Everything the comments tab needs to read/write shared annotations.
// Absent (e.g. in the test harness, or when signed out) the tab is hidden.
export interface AnnotationContext {
  docKey: string;
  appId: string;
  sequenceId: string;
  docPath: string;
  docTitle: string;
  user: { uid: string; email: string; role?: string };
}

interface PdfOutlinePanelProps {
  // Called once on mount with a setter; the viewer pushes the loaded PDF.js
  // document through it. This avoids re-rendering the viewer component, whose
  // render must stay quiet (see SimplePDFViewer notes on plugin factories).
  registerDocSetter: (setter: (doc: any | null) => void) => void;
  // Navigate the viewer to a 0-based page index (adds to page history).
  onNavigate: (pageIndex: number) => void;
  annotation?: AnnotationContext;
  // Reads the viewer's current 1-based page without coupling to its render cycle.
  getCurrentPage?: () => number;
}

// Lazily-rendered page thumbnail: draws the page onto a canvas only when it
// scrolls into view, so large dossiers don't render hundreds of pages upfront.
function PageThumbnail({ doc, pageIndex, onClick }: { doc: any; pageIndex: number; onClick: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const renderedRef = useRef(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    renderedRef.current = false;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !renderedRef.current) {
            renderedRef.current = true;
            observer.disconnect();
            doc
              .getPage(pageIndex + 1) // PDF.js getPage is 1-based
              .then((page: any) => {
                const canvas = canvasRef.current;
                if (!canvas) return;
                const baseViewport = page.getViewport({ scale: 1 });
                const scale = 140 / baseViewport.width;
                const viewport = page.getViewport({ scale });
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext('2d');
                if (ctx) page.render({ canvasContext: ctx, viewport });
              })
              .catch((err: any) => console.warn('Thumbnail render failed for page', pageIndex + 1, err));
          }
        });
      },
      { rootMargin: '300px' }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [doc, pageIndex]);

  return (
    <div ref={containerRef} className="pdf-thumb" onClick={onClick} title={`Page ${pageIndex + 1}`}>
      <canvas ref={canvasRef} className="pdf-thumb-canvas" />
      <div className="pdf-thumb-label">{pageIndex + 1}</div>
    </div>
  );
}

// Own bookmark/outline + thumbnails panel built directly on the PDF.js API.
// Replaces @react-pdf-viewer/default-layout's sidebar entirely, whose tabs
// remount on re-renders (bookmarks collapse/disappear + flicker).
export function PdfOutlinePanel({ registerDocSetter, onNavigate, annotation, getCurrentPage }: PdfOutlinePanelProps) {
  const [doc, setDoc] = useState<any | null>(null);
  const [outline, setOutline] = useState<OutlineItem[] | null>(null);
  const [isOpen, setIsOpen] = useState(true);
  const [activeTab, setActiveTab] = useState<'bookmarks' | 'pages' | 'comments'>('bookmarks');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Annotation | null>(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);
  const docRef = useRef<any | null>(null);
  docRef.current = doc;
  const numPages: number = doc?.numPages || 0;

  // Live shared annotations for this document (all reviewers see the same list)
  useEffect(() => {
    if (!annotation) {
      setAnnotations([]);
      return;
    }
    const unsubscribe = subscribeToAnnotations(annotation.docKey, setAnnotations);
    return unsubscribe;
  }, [annotation?.docKey]);

  const handleAddComment = useCallback(async () => {
    if (!annotation || !newComment.trim() || savingComment) return;
    const page = getCurrentPage ? getCurrentPage() : 1;
    setSavingComment(true);
    setCommentError(null);
    try {
      await addAnnotation({
        docKey: annotation.docKey,
        appId: annotation.appId,
        sequenceId: annotation.sequenceId,
        docPath: annotation.docPath,
        docTitle: annotation.docTitle,
        page,
        text: newComment.trim(),
        user: annotation.user,
      });
      setNewComment('');
    } catch (err: any) {
      console.error('Failed to add comment:', err);
      setCommentError(`Could not add comment: ${err?.message || err}`);
    } finally {
      setSavingComment(false);
    }
  }, [annotation, newComment, savingComment, getCurrentPage]);

  const handleToggleResolved = useCallback(
    (a: Annotation) => {
      if (!annotation) return;
      setCommentError(null);
      setAnnotationResolved(a, !a.resolved, annotation.user).catch((err: any) => {
        console.error('Failed to update comment:', err);
        setCommentError(`Could not update comment: ${err?.message || err}`);
      });
    },
    [annotation]
  );

  // Deleting requires the user to re-enter their password (identity confirmation,
  // recorded in the audit trail). Trash click opens the confirm box; this runs it.
  const handleConfirmDelete = useCallback(async () => {
    if (!annotation || !deleteTarget || !deletePassword || deleting) return;
    setDeleting(true);
    setCommentError(null);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not signed in');
      const credential = EmailAuthProvider.credential(annotation.user.email, deletePassword);
      await reauthenticateWithCredential(currentUser, credential);
      await deleteAnnotation(deleteTarget, annotation.user);
      setDeleteTarget(null);
      setDeletePassword('');
    } catch (err: any) {
      console.error('Failed to delete comment:', err);
      const msg = /wrong-password|invalid-credential|invalid-login/i.test(err?.code || err?.message || '')
        ? 'Incorrect password - deletion cancelled.'
        : `Could not delete comment: ${err?.message || err}`;
      setCommentError(msg);
    } finally {
      setDeleting(false);
    }
  }, [annotation, deleteTarget, deletePassword, deleting]);

  // Rail icon click: switch section, or toggle the panel when re-clicking the active one
  const handleRailClick = useCallback(
    (tab: 'bookmarks' | 'pages' | 'comments') => {
      if (isOpen && activeTab === tab) {
        setIsOpen(false);
      } else {
        setActiveTab(tab);
        setIsOpen(true);
      }
    },
    [isOpen, activeTab]
  );

  const sectionTitle = activeTab === 'bookmarks' ? 'Bookmarks' : activeTab === 'pages' ? 'Pages' : 'Comments';

  const openCount = annotations.filter((a) => !a.resolved).length;

  useEffect(() => {
    registerDocSetter((newDoc) => setDoc(newDoc));
    // registerDocSetter is intentionally stable in the parent
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the outline whenever the document changes; expand everything by default
  useEffect(() => {
    let cancelled = false;
    if (!doc) {
      setOutline(null);
      return;
    }
    doc
      .getOutline()
      .then((items: OutlineItem[] | null) => {
        if (cancelled) return;
        setOutline(items || []);
        setExpanded(new Set()); // Collapsed by default - user expands what they need
      })
      .catch((err: any) => {
        if (!cancelled) {
          console.warn('Failed to load PDF outline:', err);
          setOutline([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [doc]);

  const toggleExpanded = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const navigateToItem = useCallback(
    async (item: OutlineItem) => {
      const currentDoc = docRef.current;
      if (!currentDoc) return;
      try {
        let dest: any = item.dest;
        if (typeof dest === 'string') {
          dest = await currentDoc.getDestination(dest);
        }
        if (Array.isArray(dest) && dest.length > 0) {
          const pageIndex = await currentDoc.getPageIndex(dest[0]);
          onNavigate(pageIndex);
        }
      } catch (err) {
        console.warn('Failed to navigate to bookmark:', item.title, err);
      }
    },
    [onNavigate]
  );

  const renderItems = (items: OutlineItem[], prefix: string, depth: number) => (
    <ul className="pdf-outline-list" data-depth={depth}>
      {items.map((item, i) => {
        const id = prefix ? `${prefix}-${i}` : `${i}`;
        const hasChildren = item.items && item.items.length > 0;
        const isExpanded = expanded.has(id);
        return (
          <li key={id} className="pdf-outline-item">
            <div className="pdf-outline-row" style={{ paddingLeft: `${depth * 14}px` }}>
              {hasChildren ? (
                <button
                  className="pdf-outline-toggle"
                  onClick={() => toggleExpanded(id)}
                  title={isExpanded ? 'Collapse' : 'Expand'}
                >
                  {isExpanded ? <ChevronDown className="icon" /> : <ChevronRight className="icon" />}
                </button>
              ) : (
                <span className="pdf-outline-toggle-spacer" />
              )}
              {item.url ? (
                // External-URL bookmark: render as a real link so the existing
                // document-level link interceptor handles it like any PDF link.
                <a className="pdf-outline-link" href={item.url} title={item.title}>
                  {item.title}
                </a>
              ) : (
                <button
                  className="pdf-outline-link"
                  onClick={() => navigateToItem(item)}
                  title={item.title}
                >
                  {item.title}
                </button>
              )}
            </div>
            {hasChildren && isExpanded && renderItems(item.items, id, depth + 1)}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="pdf-outline-wrap">
      {/* Vertical icon rail - always visible, like classic viewer sidebars */}
      <div className="pdf-outline-railbar">
        <button
          className={`pdf-outline-rail-tab ${isOpen && activeTab === 'bookmarks' ? 'active' : ''}`}
          onClick={() => handleRailClick('bookmarks')}
          title="Bookmarks"
        >
          <ListTree className="icon" />
        </button>
        <button
          className={`pdf-outline-rail-tab ${isOpen && activeTab === 'pages' ? 'active' : ''}`}
          onClick={() => handleRailClick('pages')}
          title="Page thumbnails"
        >
          <Image className="icon" />
        </button>
        {annotation && (
          <button
            className={`pdf-outline-rail-tab ${isOpen && activeTab === 'comments' ? 'active' : ''}`}
            onClick={() => handleRailClick('comments')}
            title="Review comments"
          >
            <MessageSquare className="icon" />
            {openCount > 0 && <span className="pdf-comment-count rail">{openCount}</span>}
          </button>
        )}
      </div>

      {isOpen && (
        <div className="pdf-outline-panel">
          <div className="pdf-outline-header">
            <span className="pdf-outline-title">{sectionTitle}</span>
            <button className="pdf-outline-close" onClick={() => setIsOpen(false)} title="Hide panel">
              <PanelLeftClose className="icon" />
            </button>
          </div>
          <div className="pdf-outline-body">
        {activeTab === 'bookmarks' ? (
          outline === null ? (
            <div className="pdf-outline-empty">Loading…</div>
          ) : outline.length === 0 ? (
            <div className="pdf-outline-empty">No bookmarks in this document</div>
          ) : (
            renderItems(outline, '', 0)
          )
        ) : activeTab === 'pages' ? (
          doc && numPages > 0 ? (
            <div className="pdf-thumb-list">
              {Array.from({ length: numPages }, (_, i) => (
                <PageThumbnail key={i} doc={doc} pageIndex={i} onClick={() => onNavigate(i)} />
              ))}
            </div>
          ) : (
            <div className="pdf-outline-empty">Loading…</div>
          )
        ) : annotation ? (
          <div className="pdf-comments">
            <div className="pdf-comment-add">
              <textarea
                className="pdf-comment-input"
                placeholder="Add a comment on the page currently in view…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows={3}
              />
              <button
                className="pdf-comment-add-button"
                onClick={handleAddComment}
                disabled={!newComment.trim() || savingComment}
              >
                {savingComment ? 'Adding…' : 'Add comment'}
              </button>
            </div>
            {commentError && <div className="pdf-comment-error">{commentError}</div>}
            {annotations.length === 0 ? (
              <div className="pdf-outline-empty">No comments yet on this document</div>
            ) : (
              <ul className="pdf-comment-list">
                {annotations.map((a) => {
                  const canDelete = a.authorUid === annotation.user.uid || annotation.user.role === 'admin';
                  return (
                    <li key={a.id} className={`pdf-comment-item ${a.resolved ? 'resolved' : ''}`}>
                      <div className="pdf-comment-row">
                        <button
                          className={`pdf-comment-check ${a.resolved ? 'checked' : ''}`}
                          title={a.resolved ? `Resolved by ${a.resolvedBy || 'unknown'} - click to reopen` : 'Mark as resolved'}
                          onClick={() => handleToggleResolved(a)}
                        >
                          {a.resolved ? <CheckSquare className="icon" /> : <Square className="icon" />}
                        </button>
                        <div className="pdf-comment-content" onClick={() => onNavigate(a.page - 1)}>
                          <div className="pdf-comment-text">{a.text}</div>
                          <div className="pdf-comment-meta">
                            p.{a.page} · {a.authorEmail.split('@')[0]} · {a.createdAt.toLocaleDateString()}
                            {a.resolved && a.resolvedBy && ` · resolved by ${a.resolvedBy.split('@')[0]}`}
                          </div>
                        </div>
                        {canDelete && (
                          <button
                            className="pdf-comment-delete"
                            title="Delete comment (requires password confirmation)"
                            onClick={() => {
                              setDeleteTarget(a);
                              setDeletePassword('');
                              setCommentError(null);
                            }}
                          >
                            <Trash2 className="icon" />
                          </button>
                        )}
                      </div>
                      {deleteTarget?.id === a.id && (
                        <div className="pdf-comment-confirm">
                          <div className="pdf-comment-confirm-label">
                            Confirm deletion - enter your password
                          </div>
                          <input
                            type="password"
                            className="pdf-comment-confirm-input"
                            placeholder="Your login password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleConfirmDelete();
                            }}
                            autoFocus
                            autoComplete="new-password"
                            name="confirm-delete-password"
                          />
                          <div className="pdf-comment-confirm-actions">
                            <button
                              className="pdf-comment-confirm-delete"
                              onClick={handleConfirmDelete}
                              disabled={!deletePassword || deleting}
                            >
                              {deleting ? 'Deleting…' : 'Delete'}
                            </button>
                            <button
                              className="pdf-comment-confirm-cancel"
                              onClick={() => {
                                setDeleteTarget(null);
                                setDeletePassword('');
                              }}
                              disabled={deleting}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
